import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { verifyOTP } from '../../api/authAPI';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, CheckCircle, AlertCircle, ArrowLeft, Lock, User, Phone, Calendar, Image as ImageIcon } from 'lucide-react';

const ROLE_OPTIONS = [
  {
    value: 'worker',
    label: 'Worker',
    description: 'Find jobs, showcase CVs, and apply to opportunities.',
  },
  {
    value: 'provider',
    label: 'Provider',
    description: 'Post jobs, review applicants, and manage hiring.',
  },
];

const WORKER_CATEGORY_OPTIONS = [
  { value: 'graduate',   label: 'Graduate' },
  { value: 'student',    label: 'Student' },
  { value: 'housewife',  label: 'Homemaker' },
  { value: 'unemployed', label: 'Searching for work' },
  { value: 'other',      label: 'Other' },
];

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, loginWithData, isAuthenticated, loading: authLoading } = useAuth();

  const [formType,   setFormType]   = useState(searchParams.get('tab') === 'register' ? 'register' : 'login');
  const [regStep,    setRegStep]    = useState('details');
  const [submitting, setSubmitting] = useState(false);
  const [formError,  setFormError]  = useState('');
  const [formData,   setFormData]   = useState({
    name: '', email: '', password: '', phone: '', dateOfBirth: '', profileImage: '',
  });

  const [otpCode,      setOtpCode]      = useState('');
  const [otpEmail,     setOtpEmail]     = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError,     setOtpError]     = useState('');
  const [devOTP,       setDevOTP]       = useState('');
  const [roleChoice,   setRoleChoice]   = useState('worker');
  const [workerCategory, setWorkerCategory] = useState('graduate');

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormError('Please choose an image file for profile picture.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormError('');
      setFormData((prev) => ({ ...prev, profileImage: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const switchForm = (type) => {
    setFormType(type);
    setFormError('');
    setRegStep('details');
    setOtpCode('');
    setOtpError('');
    setRoleChoice('worker');
    setWorkerCategory('graduate');
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setFormError(''); setSubmitting(true);
    try {
      await login(formData.email, formData.password);
      navigate('/');
    } catch (err) {
      const errorMsg = err.isNetworkError 
        ? 'Cannot reach the API server. Please ensure the backend is running on http://localhost:5000'
        : (err.response?.data?.message || err.message || 'Login failed');
      setFormError(errorMsg);
    } finally { setSubmitting(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setFormError('');
    const { name, email, password, phone, dateOfBirth, profileImage } = formData;
    if (!name || !email || !password || !phone || !dateOfBirth || !profileImage) {
      setFormError('Please fill in all fields'); return;
    }
    if (!roleChoice) {
      setFormError('Please choose whether you are registering as a worker or provider.');
      return;
    }
    if (roleChoice === 'worker' && !workerCategory) {
      setFormError('Please select the worker category that fits you best.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name,
        email,
        password,
        phone,
        dateOfBirth,
        role: roleChoice,
        profileImage: profileImage || '',
      };

      if (roleChoice === 'worker') {
        payload.category = workerCategory;
      }

      const res = await register({
        ...payload,
      });
      if (res.pendingVerification) {
        setOtpEmail(res.email);
        if (res.devOTP) setDevOTP(res.devOTP);
        setRegStep('verify-otp');
      }
    } catch (err) {
      const errorMsg = err.isNetworkError 
        ? 'Cannot reach the API server. Please ensure the backend is running on http://localhost:5000'
        : (err.response?.data?.message || err.message || 'Registration failed');
      setFormError(errorMsg);
    } finally { setSubmitting(false); }
  };

  const handleVerifyOTP = async () => {
    setOtpError('');
    if (otpCode.length !== 6) { setOtpError('Please enter the 6-digit OTP'); return; }
    setOtpVerifying(true);
    try {
      const res = await verifyOTP(formData.email, otpCode);
      loginWithData(res.data);
      navigate('/');
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Invalid or expired OTP.');
    } finally { setOtpVerifying(false); }
  };

  const maxDOB = new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0];

  const slideVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2, ease: 'easeIn' } }
  };

  return (
    <div className="flex w-full min-h-screen" style={{ background: 'var(--background)' }}>
      {/* LEFT HALF */}
      <div className="hidden md:flex relative w-1/2 flex-col justify-between overflow-hidden">
        <div 
          className="absolute inset-0 w-full h-full bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/team/form.png)' }}
        ></div>
        <div className="absolute inset-0 bg-black/60"></div>
        
        <div className="relative z-10 p-12 flex flex-col h-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity w-fit">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xl shadow-sm" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
              M
            </div>
            <span className="text-2xl font-extrabold tracking-tight text-white">
              MattersUrSkills
            </span>
          </Link>

          <div className="flex-1 flex flex-col justify-center">
            <h1 className="text-5xl xl:text-6xl font-extrabold text-white leading-tight">
              Find Opportunities.<br />
              Build Your <span style={{ color: '#d1d5db' }}>Future.</span>
            </h1>
            <div className="w-24 h-1.5 mt-8 mb-6 rounded-full" style={{ background: 'var(--primary)' }}></div>
            <p className="text-lg xl:text-xl text-gray-300 max-w-lg">
              Join a platform that connects talent with opportunity and helps you grow.
            </p>
          </div>

          <div className="mt-auto pt-8">
            <p className="text-sm text-gray-400 font-medium flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-gray-400" />
              Trusted by professionals and businesses worldwide.
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT HALF */}
      <div className="flex w-full md:w-1/2 flex-col items-center justify-center p-6 sm:p-12 relative min-h-screen overflow-y-auto">
        <div className="w-full max-w-md space-y-8 glass-panel p-8 sm:p-10 rounded-3xl shadow-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--foreground)' }}>
              {formType === 'login' ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="mt-2 text-sm" style={{ color: 'var(--muted-foreground)' }}>
              {formType === 'login' ? 'Login to your account to continue' : 'Join us to start your journey'}
            </p>
          </div>

          {/* Tab Switcher */}
          {regStep !== 'verify-otp' && (
            <div className="flex p-1 rounded-xl" style={{ background: 'var(--muted)' }}>
              <button
                type="button"
                onClick={() => switchForm('login')}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${formType === 'login' ? 'shadow-sm' : ''}`}
                style={{
                  background: formType === 'login' ? 'var(--background)' : 'transparent',
                  color: formType === 'login' ? 'var(--foreground)' : 'var(--muted-foreground)'
                }}
              >
                Login
              </button>
              <button
                type="button"
                onClick={() => switchForm('register')}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${formType === 'register' ? 'shadow-sm' : ''}`}
                style={{
                  background: formType === 'register' ? 'var(--background)' : 'transparent',
                  color: formType === 'register' ? 'var(--foreground)' : 'var(--muted-foreground)'
                }}
              >
                Register
              </button>
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* LOGIN FORM */}
            {formType === 'login' && (
              <motion.div key="login" variants={slideVariants} initial="hidden" animate="visible" exit="exit">
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-1.5 relative">
                    <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--muted-foreground)' }} />
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                        required placeholder="you@example.com" className="input-field pl-10" />
                    </div>
                  </div>
                  <div className="space-y-1.5 relative">
                    <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--muted-foreground)' }} />
                      <input type="password" name="password" value={formData.password} onChange={handleInputChange}
                        required placeholder="Enter your password" className="input-field pl-10" />
                    </div>
                  </div>
                  
                  {formError && (
                    <div className="flex items-start gap-2 p-3 border rounded-lg text-sm bg-transparent" style={{ color: 'var(--destructive)', borderColor: 'var(--destructive)' }}>
                      <AlertCircle className="w-5 h-5 shrink-0" />
                      <p>{formError}</p>
                    </div>
                  )}
                  
                  <button type="submit" disabled={submitting} className="btn-primary w-full mt-2 py-3 flex items-center justify-center gap-2 font-semibold rounded-xl transition-all">
                    {submitting ? 'Signing in...' : 'Login'}
                  </button>
                  
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t" style={{ borderColor: 'var(--border)' }}></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2" style={{ background: 'var(--card)', color: 'var(--muted-foreground)' }}>or continue with</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <button type="button" className="flex items-center justify-center gap-2 py-2.5 border rounded-xl font-medium transition-colors hover:bg-black/5 dark:hover:bg-white/5" style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                      <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
                      Google
                    </button>
                    <button type="button" className="flex items-center justify-center gap-2 py-2.5 border rounded-xl font-medium transition-colors hover:bg-black/5 dark:hover:bg-white/5" style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                      <svg className="w-5 h-5" fill="#0A66C2" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                      LinkedIn
                    </button>
                  </div>
                  
                  <p className="text-center text-sm pt-4" style={{ color: 'var(--muted-foreground)' }}>
                    Don't have an account?{' '}
                    <button type="button" onClick={() => switchForm('register')} className="font-semibold hover:underline" style={{ color: 'var(--primary)' }}>
                      Register
                    </button>
                  </p>
                </form>
              </motion.div>
            )}

            {/* REGISTER DETAILS FORM */}
            {formType === 'register' && regStep === 'details' && (
              <motion.div key="register-details" variants={slideVariants} initial="hidden" animate="visible" exit="exit">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5 relative">
                      <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Full Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                          required placeholder="Your full name" className="input-field pl-9" />
                      </div>
                    </div>
                    <div className="space-y-1.5 relative">
                      <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                        <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                          required placeholder="you@example.com" className="input-field pl-9" />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5 relative">
                    <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                      <input type="password" name="password" value={formData.password} onChange={handleInputChange}
                        required placeholder="Minimum 6 characters" minLength={6} className="input-field pl-9" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5 relative">
                      <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Phone Number</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                          required placeholder="+91 98765 43210" className="input-field pl-9" />
                      </div>
                    </div>
                    <div className="space-y-1.5 relative">
                      <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                        Date of Birth <span className="font-normal" style={{ color: 'var(--muted-foreground)' }}>(18+)</span>
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                        <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange}
                          required max={maxDOB} className="input-field pl-9" />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5 relative">
                    <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Profile Picture</label>
                    <div className="relative flex items-center">
                      <ImageIcon className="absolute left-3 w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                      <input type="file" accept="image/*" onChange={handleAvatarChange} required className="input-field pl-9 text-sm" style={{ color: 'var(--muted-foreground)' }} />
                    </div>
                  </div>
                  <div className="space-y-2 pt-2">
                    <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Account Type</label>
                    <div className="grid sm:grid-cols-2 gap-3 mt-1">
                      {ROLE_OPTIONS.map((option) => (
                        <button
                          type="button"
                          key={option.value}
                          onClick={() => setRoleChoice(option.value)}
                          className="text-left rounded-xl border p-3 transition-colors flex flex-col gap-1"
                          style={{
                            background: roleChoice === option.value ? 'var(--primary)' : 'var(--accent)',
                            borderColor: roleChoice === option.value ? 'var(--primary)' : 'var(--border)',
                            color: roleChoice === option.value ? 'var(--primary-foreground)' : 'var(--foreground)'
                          }}
                        >
                          <span className="text-sm font-semibold flex items-center gap-2">
                            {option.label}
                            {roleChoice === option.value && <CheckCircle className="w-4 h-4" />}
                          </span>
                          <span className="text-xs opacity-90" style={{ color: roleChoice === option.value ? 'var(--primary-foreground)' : 'var(--muted-foreground)' }}>{option.description}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  {roleChoice === 'worker' && (
                    <div className="space-y-1.5 pt-1">
                      <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Worker Category</label>
                      <select className="input-field" value={workerCategory} onChange={(e) => setWorkerCategory(e.target.value)}>
                        {WORKER_CATEGORY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <label className="flex items-start gap-3 cursor-pointer pt-2">
                    <input type="checkbox" required className="mt-1 w-4 h-4 rounded border-gray-300" style={{ accentColor: 'var(--primary)' }} />
                    <span className="text-sm leading-tight" style={{ color: 'var(--muted-foreground)' }}>I confirm I am 18 years or older and agree to the Terms of Service.</span>
                  </label>
                  
                  {formError && (
                    <div className="flex items-start gap-2 p-3 border rounded-lg text-sm bg-transparent" style={{ color: 'var(--destructive)', borderColor: 'var(--destructive)' }}>
                      <AlertCircle className="w-5 h-5 shrink-0" />
                      <p>{formError}</p>
                    </div>
                  )}
                  
                  <button type="submit" disabled={submitting} className="btn-primary w-full py-3 mt-2 font-semibold rounded-xl transition-all">
                    {submitting ? 'Creating account...' : 'Create Account'}
                  </button>
                  
                  <p className="text-center text-sm pt-4" style={{ color: 'var(--muted-foreground)' }}>
                    Already have an account?{' '}
                    <button type="button" onClick={() => switchForm('login')} className="font-semibold hover:underline" style={{ color: 'var(--primary)' }}>
                      Login
                    </button>
                  </p>
                </form>
              </motion.div>
            )}

            {/* OTP VERIFICATION STEP */}
            {formType === 'register' && regStep === 'verify-otp' && (
              <motion.div key="verify-otp" variants={slideVariants} initial="hidden" animate="visible" exit="exit" className="space-y-6">
                <div className="text-center pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--muted)' }}>
                    <Mail className="w-6 h-6" style={{ color: 'var(--foreground)' }} />
                  </div>
                  <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>Check your email</h2>
                  <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    We sent a 6-digit verification code to<br/>
                    <span className="font-semibold" style={{ color: 'var(--foreground)' }}>{otpEmail}</span>
                  </p>
                </div>
                
                {devOTP && (
                  <div className="rounded-xl p-4 text-center border" style={{ background: 'var(--accent)', borderColor: 'var(--border)' }}>
                    <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                      Dev Mode
                    </p>
                    <p className="text-3xl font-black tracking-[0.5em] ml-[0.5em]" style={{ color: 'var(--foreground)' }}>{devOTP}</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-center" style={{ color: 'var(--foreground)' }}>Enter verification code</label>
                  <input
                    type="text"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="------"
                    className="input-field text-center text-3xl font-bold tracking-[0.5em] py-4"
                    autoFocus
                  />
                </div>
                
                {otpError && (
                  <div className="flex items-start gap-2 p-3 border rounded-lg text-sm bg-transparent" style={{ color: 'var(--destructive)', borderColor: 'var(--destructive)' }}>
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <p>{otpError}</p>
                  </div>
                )}
                
                <div className="space-y-3 pt-2">
                  <button onClick={handleVerifyOTP} disabled={otpVerifying} className="btn-primary w-full py-3 font-semibold rounded-xl transition-all">
                    {otpVerifying ? 'Verifying...' : 'Verify Email'}
                  </button>
                  <button
                    onClick={() => { setRegStep('details'); setOtpCode(''); setOtpError(''); }}
                    className="w-full flex items-center justify-center gap-2 text-sm py-2 hover:underline transition-all"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to registration
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Login;